import { ShoppingCart, Trash2 } from "lucide-react";
import { useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { useCreateDepositDialog } from "@/features/deposits";
import { useIsMobile } from "@/shared/lib/use-mobile";
import { InfoTooltip } from "@/shared/ui/info-tooltip";
import { Button } from "@/shared/ui/kit/button";
import { Skeleton } from "@/shared/ui/kit/skeleton";
import { UnifiedDrawer } from "@/shared/ui/unified-drawer";

import { useCartModel } from "./model/use-cart-model";
import { CartEmpty } from "./ui/cart-empty";
import { CartListSkeleton } from "./ui/cart-item-skeleton";
import { CartList } from "./ui/cart-list";
import { CartSummary } from "./ui/cart-summary";

type CartSheetProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  scrollOffset?: number;
  onScrollOffsetChange?: (offset: number) => void;
};

function CartSummarySkeleton() {
  return (
    <div className="space-y-4 p-4">
      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <Skeleton className="h-4 w-12" />
          <Skeleton className="h-4 w-6" />
        </div>
        <div className="flex justify-between text-lg font-semibold">
          <Skeleton className="h-5 w-12" />
          <Skeleton className="h-5 w-20" />
        </div>
      </div>
      <Skeleton className="h-10 w-full rounded-md" />
    </div>
  );
}

export function CartSheet({
  isOpen,
  onOpenChange,
  scrollOffset = 0,
  onScrollOffsetChange,
}: CartSheetProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const createDepositDialog = useCreateDepositDialog();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const cart = useCartModel();

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open && scrollContainerRef.current && onScrollOffsetChange) {
        onScrollOffsetChange(scrollContainerRef.current.scrollTop);
      }
      onOpenChange(open);
    },
    [onOpenChange, onScrollOffsetChange],
  );

  const content = cart.isPending ? (
    <CartListSkeleton count={3} />
  ) : cart.isError ? (
    <div className="text-center py-16 text-destructive px-6">
      {t("cart.errors.loadFailed")}
    </div>
  ) : cart.isEmpty ? (
    <CartEmpty onClose={() => onOpenChange(false)} />
  ) : (
    <CartList
      items={cart.items}
      isItemPending={cart.isItemPending}
      onProductClick={() => handleOpenChange(false)}
      scrollContainerRef={scrollContainerRef}
      cursorRef={cart.cursorRef}
      isFetchingNextPage={cart.isFetchingNextPage}
      savedScrollOffset={scrollOffset}
    />
  );

  const footer = cart.isPending ? (
    <div className="border-t p-4">
      <CartSummarySkeleton />
    </div>
  ) : (
    !cart.isEmpty &&
    !cart.isError && (
      <UnifiedDrawer.Footer className="p-4">
        <CartSummary
          totalCents={cart.totals.totalCents}
          itemCount={cart.totals.itemCount}
          hasOutOfStock={cart.hasOutOfStock}
          isPending={cart.isAnyMutating}
          onTopUp={createDepositDialog.open}
        />
      </UnifiedDrawer.Footer>
    )
  );

  const title = !cart.isEmpty && !cart.isPending && (
    <div className="flex items-center justify-between w-full">
      <div className="flex items-center gap-2">
        <ShoppingCart className="size-4" />
        <span className="inline-flex items-center gap-1.5">
          {t("cart.title")}
          <InfoTooltip tKey="cart.info.optimisticHint" />
        </span>
      </div>
      <Button
        variant="ghost"
        size="icon-sm"
        onClick={cart.clearCart}
        disabled={cart.isMutating}
        aria-label={t("cart.clear")}
        data-testid="cart-clear-button"
        className="shrink-0 text-destructive rounded-full hover:text-destructive hover:bg-destructive/10"
      >
        <Trash2 className="size-4" />
      </Button>
    </div>
  );

  const direction = isMobile ? "bottom" : "right";

  return (
    <UnifiedDrawer
      open={isOpen}
      onOpenChange={handleOpenChange}
      direction={direction}
    >
      <UnifiedDrawer.Content
        className={isMobile ? undefined : "w-full max-w-[450px] rounded-l-2xl"}
      >
        {title && (
          <UnifiedDrawer.Header className="shrink-0">
            <UnifiedDrawer.Title className="text-base font-semibold">
              {title}
            </UnifiedDrawer.Title>
          </UnifiedDrawer.Header>
        )}
        <UnifiedDrawer.Body
          ref={scrollContainerRef}
          className="overflow-y-auto p-0 no-scrollbar"
        >
          {content}
        </UnifiedDrawer.Body>
        {footer}
      </UnifiedDrawer.Content>
    </UnifiedDrawer>
  );
}
